/**
 * Impersonation banner (CHAP-172). Persistent, high-visibility bar shown on
 * every authed page when the session is an admin impersonation. Reads the
 * `impersonating` flag from /api/me; "Stop" ends the impersonation and reloads.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../lib/api.js';
import type { UserMe } from '../../../shared/types.js';

export function ImpersonationBanner() {
  const { data } = useQuery<UserMe, Error>({
    queryKey: ['me'],
    queryFn: () => apiGet<UserMe>('/api/me'),
  });
  const [stopping, setStopping] = useState(false);

  if (!data?.impersonating) return null;

  async function stop() {
    setStopping(true);
    try {
      await apiPost('/api/admin/impersonate/stop');
    } catch {
      // fall through to reload regardless
    }
    window.location.reload();
  }

  return (
    <div
      role="alert"
      className="sticky top-0 z-40 bg-amber-500 text-amber-950 px-3 sm:px-4 lg:px-6 py-2 flex items-center justify-between gap-3 flex-wrap text-sm font-medium min-w-0"
    >
      <span className="flex items-center gap-2 min-w-0">
        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        <span className="truncate">
          Viewing as <strong>{data.user.email}</strong>
          <span className="hidden sm:inline"> · impersonated by {data.impersonating.by}</span>
        </span>
      </span>
      <button
        type="button"
        onClick={stop}
        disabled={stopping}
        className="flex-shrink-0 px-3 py-1 rounded bg-amber-950 text-amber-50 text-xs font-semibold hover:bg-amber-900 disabled:opacity-50"
      >
        {stopping ? 'Stopping…' : 'Stop impersonating'}
      </button>
    </div>
  );
}
